import { HttpEventType, HttpResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    inject,
} from '@angular/core';
import {
    IImport,
    ImportEventType,
    ImportFacade,
} from '@wdx/kamino/api-service';
import { KaminoModalWrapperComponent } from '@wdx/kamino/ui';
import { WdxDestroyClass } from '@wdx/shared/utils';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { KaminoFileUploaderComponent } from '../shared/components/file-uploader/file-uploader.component';
import { KaminoFileUploaderService } from '../shared/components/file-uploader/file-uploader.service';

@Component({
    selector: 'kamino-file-upload',
    imports: [KaminoModalWrapperComponent, KaminoFileUploaderComponent],
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KaminoFeaturesFileUploadComponent
    extends WdxDestroyClass
    implements OnInit
{
    private fileUploaderService = inject(KaminoFileUploaderService);
    private importFacade = inject(ImportFacade);

    @Input() path!: string;
    @Input() acceptedFileTypes!: string;

    overwriteLocalChanges$ = new BehaviorSubject<boolean>(false);

    ngOnInit(): void {
        combineLatest([
            this.fileUploaderService.files$.pipe(
                filter((files) => Boolean(files.length)),
            ),
            this.overwriteLocalChanges$,
        ])
            .pipe(
                takeUntil(this.destroyed$),
                tap(([files, _]) => {
                    this.fileUploaderService.setDisabled(true);
                }),
                switchMap(([files, overwriteLocalChanges]) =>
                    this.importFacade.sendData$(
                        files[0],
                        this.path,
                        overwriteLocalChanges,
                    ),
                ),
            )
            .subscribe({
                next: (res) => {
                    if (res.type === HttpEventType.UploadProgress) {
                        this.fileUploaderService.setUploadProgress(
                            (100 * res.loaded) / (res?.total as number),
                        );
                    }

                    if (res instanceof HttpResponse) {
                        this.fileUploaderService.setDisabled(false);
                        this.fileUploaderService.setUploadedFile(
                            res.body as IImport,
                        );
                    }
                },
                error: (error) => {
                    this.fileUploaderService.addError(error);
                    this.fileUploaderService.setDisabled(false);
                },
            });
    }

    onOverwriteLocalChanges(value: boolean): void {
        this.overwriteLocalChanges$.next(value);
    }

    onFileEvent(fileEvent: ImportEventType): void {
        const ID = (this.fileUploaderService.uploadedFile as IImport)?.id;
        this.fileUploaderService.setFiledEvent(true);
        this.importFacade
            .importEvent$(fileEvent, ID)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    this.fileUploaderService.setFiledEvent(false);
                    this.fileUploaderService.dismiss();
                },
                error: () => {
                    this.fileUploaderService.setFiledEvent(false);
                },
            });
    }
}
